import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { UploadedPart } from './types';

// File extensions a 3D-part upload accepts. STL is voxelized / parsed directly;
// STEP/STP is tessellated to a mesh server-side (build123d) before use. Distinct
// from the chat attachment list (fileTypes.ts) — that's GLB/OBJ/FBX/STL with no
// STEP — so the set lives here rather than reusing getMeshFileType (which
// buckets unknown types to 'glb').
export const ACCEPTED_PART_EXTENSIONS = ['stl', 'step', 'stp'] as const;

function extensionOf(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

export function isAcceptedPartFile(filename: string): boolean {
  return (ACCEPTED_PART_EXTENSIONS as readonly string[]).includes(
    extensionOf(filename),
  );
}

export interface UsePartUploadOptions {
  /** Path segment under `{uid}/` in the `meshes` bucket (e.g. 'lattice-fill').
   *  Lets each feature namespace its uploads; the leading `{uid}` segment is
   *  always prepended here to satisfy the meshes-bucket RLS. Default 'parts'. */
  pathPrefix?: string;
}

export interface UsePartUpload {
  uploading: boolean;
  /** REAL upload progress, 0–1 (fraction of bytes sent), from XHR
   *  `upload.onprogress`. Meaningful only while `uploading`; reset to 0 at the
   *  start of every upload. */
  progress: number;
  error: string | null;
  upload: (file: File) => Promise<UploadedPart | null>;
}

// Storage REST target — the same values the supabase-js client is built from
// (src/lib/supabase.ts), read here so the XHR path posts to the exact endpoint
// the SDK would.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined;

/**
 * Upload a 3D part to the private `meshes` bucket under a user-scoped,
 * conversation-independent path (`{uid}/{pathPrefix}/{id}.{ext}`). Works BEFORE
 * a conversation exists (e.g. on the Prompt view), so it can't use the chat
 * useAttachmentUploads hook (which keys the path on conversation.id). The `{uid}`
 * first segment satisfies the meshes-bucket RLS (foldername[1] = auth.uid). An
 * edge function later mints a service-role signed URL on the returned meshRef —
 * the browser never needs to read the object back.
 *
 * The upload goes through XMLHttpRequest rather than `supabase.storage.upload()`
 * so we get REAL byte-level progress: the storage SDK's fetch path emits no
 * progress events, so a bar driven off it can only fake it. XHR POSTs the raw
 * file to the IDENTICAL Storage REST endpoint the SDK uses, with the user's
 * JWT — RLS is satisfied exactly as the SDK call satisfied it.
 */
export function usePartUpload(
  options: UsePartUploadOptions = {},
): UsePartUpload {
  const { pathPrefix = 'parts' } = options;
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File): Promise<UploadedPart | null> => {
      setError(null);
      const ext = extensionOf(file.name);
      if (!isAcceptedPartFile(file.name)) {
        setError('Upload an .stl, .step, or .stp file.');
        return null;
      }
      if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
        setError('Storage is not configured.');
        return null;
      }
      setUploading(true);
      setProgress(0);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        const accessToken = session?.access_token;
        if (!userId || !accessToken) {
          setError('Sign in to upload a part.');
          return null;
        }
        const id = crypto.randomUUID();
        const meshRef = `${userId}/${pathPrefix}/${id}.${ext}`;

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(
            'POST',
            `${SUPABASE_URL}/storage/v1/object/meshes/${meshRef}`,
            true,
          );
          xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
          xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY as string);
          xhr.setRequestHeader('x-upsert', 'false');
          xhr.setRequestHeader('cache-control', 'max-age=3600');
          xhr.setRequestHeader(
            'content-type',
            file.type || 'application/octet-stream',
          );
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(e.loaded / e.total);
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setProgress(1);
              resolve();
            } else {
              let message = `Upload failed (${xhr.status}).`;
              try {
                const body = JSON.parse(xhr.responseText);
                message = body.message || body.error || message;
              } catch {
                /* non-JSON error body — keep the status message */
              }
              reject(new Error(message));
            }
          };
          xhr.onerror = () => reject(new Error('Network error during upload.'));
          xhr.onabort = () => reject(new Error('Upload cancelled.'));
          xhr.send(file);
        });

        return { meshRef, meshName: file.name };
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed.');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [pathPrefix],
  );

  return { uploading, progress, error, upload };
}
